import React, { useState, useEffect } from 'react';
import './RoleManagement.css';

const baseUrl = 'https://apiportal.mbsays.com/public/api';
const token = sessionStorage.getItem('authToken') || 'admin_token';
const headers = {
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${token}`
};
// İzin slug'larına karşılık gelen Türkçe başlıklar
const permissionTranslations = {
  'edit users': 'Kullanıcıları Düzenle',
  'edit brands': 'Markaları Düzenle',
  'edit forms': 'Form Taleplerini Düzenle',
  'izin': 'İzin Taleplerini Yönet',
  'edit expenses': 'Masrafları Düzenle',
  'edit notes': 'Notları Düzenle',
  'edit trends': 'Trendleri Düzenle',
  'edit emergency contacts': 'Acil Durum Kişilerini Düzenle',
  'view emergency contacts': 'Acil Durum Kişilerini Görüntüle'
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [newRole, setNewRole] = useState("");
  const [loading, setLoading] = useState(true);

  // Tüm izinleri çek (GET /api/permissions)
  const fetchAllPermissions = async () => {
    try {
      const response = await fetch(`${baseUrl}/permissions`, { headers });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`İzinler çekilirken hata: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      setAllPermissions(data);
    } catch (error) {
      console.error("Tüm izinler çekilirken hata oluştu:", error);
    }
  };

  // Roller ve her rolün izinlerini çek
  const fetchRoles = async () => {
    try {
      const response = await fetch(`${baseUrl}/roles`, { headers });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Rol bilgileri çekilirken hata: ${response.status} - ${errorText}`);
      }
      const rolesData = await response.json();
      const rolesWithPermissions = await Promise.all(
        rolesData.map(async (role) => {
          try {
            const resp = await fetch(`${baseUrl}/roles/${role.id}/permissions`, { headers });
            if (resp.ok) {
              const perms = await resp.json();
              return { ...role, permissions: perms };
            } else {
              return { ...role, permissions: [] };
            }
          } catch (error) {
            console.error(`Rol ${role.id} izinleri çekilirken hata:`, error);
            return { ...role, permissions: [] };
          }
        })
      );
      setRoles(rolesWithPermissions);
    } catch (error) {
      console.error("Roller çekilirken hata oluştu:", error);
    }
  };

  const loadData = async () => {
    await Promise.all([fetchRoles(), fetchAllPermissions()]);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setSelectedPermissions(prev => {
      const newSelected = { ...prev };
      roles.forEach(role => {
        if (newSelected[role.id] === undefined) {
          newSelected[role.id] = role.permissions ? role.permissions.map(p => p.name) : [];
        }
      });
      return newSelected;
    });
  }, [roles]);

  const handleAddRole = async () => {
    if (newRole.trim() !== "") {
      try {
        await fetch(`${baseUrl}/roles`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ name: newRole.trim() })
        });
        await fetchRoles();
        setNewRole("");
      } catch (error) {
        console.error("Rol eklenirken hata oluştu:", error);
      }
    }
  };

  const handleCheckboxChange = (roleId, permissionName, isChecked) => {
    setSelectedPermissions(prev => {
      const current = prev[roleId] || [];
      let updated;
      if (isChecked) {
        updated = [...current, permissionName];
      } else {
        updated = current.filter(p => p !== permissionName);
      }
      return { ...prev, [roleId]: updated };
    });
  };

  const handleUpdateRolePermissions = async (roleId) => {
    const permissions = selectedPermissions[roleId] || [];
    try {
      const response = await fetch(`${baseUrl}/roles/${roleId}/permissions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ permissions })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`İzin güncelleme hatası: ${response.status} - ${errorText}`);
      }
      await fetchRoles();
      alert("İzinler güncellendi.");
    } catch (error) {
      console.error("İzin güncellenirken hata oluştu:", error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="role-management">
      <h2>Rol & İzin Yönetimi</h2>
      <div className="create-role">
        <input
          type="text"
          placeholder="Yeni rol adı"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
        />
        <button onClick={handleAddRole}>Rol Ekle</button>
      </div>
      <div className="roles-list">
        {roles.map(role => (
          <div key={role.id} className="role-card">
            <p><strong>Rol:</strong> {role.name.charAt(0).toUpperCase() + role.name.slice(1)}</p>
            <div>
  <strong>Mevcut İzinler:</strong>
  {(role.permissions || []).length > 0 ? (
    <ul className="permission-list">
      {role.permissions.map(p => (
        <li key={p.id}>
          {permissionTranslations[p.name] || p.name.toTitleCase()}
        </li>
      ))}
    </ul>
  ) : (
    <p>Yok</p>
  )}
</div>
            <div className="permission-checkboxes">
              {allPermissions.map(perm => {
                const label =
                  permissionTranslations[perm.name] ||
                  perm.name.toTitleCase(); // fallback: büyük harfli slug

                return (
                  <div key={perm.id}>
                    <label>
                      <input
                        type="checkbox"
                        value={perm.name}
                        checked={
                          selectedPermissions[role.id] &&
                          selectedPermissions[role.id].includes(perm.name)
                        }
                        onChange={(e) =>
                          handleCheckboxChange(role.id, perm.name, e.target.checked)
                        }
                      />
                      {label}
                    </label>
                  </div>
                );
              })}

              <button onClick={() => handleUpdateRolePermissions(role.id)}>
                İzinleri Güncelle
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoleManagement;
